<template>
  <div
    class="editor-toolbar"
    v-if="showToolbar"
    role="toolbar"
    :aria-label="$t('Formatting')"
    @mousedown.prevent
  >
    <div
      v-for="group in buttonGroups"
      :key="group.group"
      class="toolbar-group"
    >
      <button
        v-for="buttonId in group.buttons"
        :key="buttonId"
        type="button"
        class="toolbar-button"
        :class="{ 'is-active': isActive(buttonId) }"
        :disabled="isDisabled(buttonId)"
        :aria-label="getLabel(buttonId)"
        :aria-pressed="isToggle(buttonId) ? String(isActive(buttonId)) : null"
        @mousedown.prevent
        @click.prevent="handleClick(buttonId)"
        @mouseenter="hoveredButton = buttonId"
        @mouseleave="hoveredButton = null"
      >
        <component :is="getIcon(buttonId)" class="toolbar-icon" aria-hidden="true" />
        <!-- The hover tooltip repeats the button's aria-label, so it is hidden
             from assistive tech to avoid announcing the name twice. -->
        <transition name="tooltip-fade">
          <span v-if="hoveredButton === buttonId" class="toolbar-tooltip" aria-hidden="true">
            {{ getLabel(buttonId) }}
            <span v-if="getShortcut(buttonId)" class="toolbar-shortcut">
              {{ getShortcut(buttonId) }}
            </span>
          </span>
        </transition>
      </button>
    </div>

    <!-- Link Dialog -->
    <div v-if="showLinkDialog" class="toolbar-dialog">
      <div class="toolbar-dialog__backdrop" @click="closeLinkDialog"></div>
      <div class="toolbar-dialog__content">
        <label class="toolbar-dialog__label">{{ $t('URL') }}</label>
        <input
          ref="linkInput"
          v-model="linkUrl"
          type="url"
          class="toolbar-dialog__input"
          :placeholder="$t('https://example.com')"
          @keydown.enter.prevent="insertLink"
          @keydown.esc="closeLinkDialog"
        />
        <div class="toolbar-dialog__actions">
          <button
            type="button"
            class="toolbar-dialog__btn toolbar-dialog__btn--cancel"
            @click="closeLinkDialog"
          >
            {{ $t('Cancel') }}
          </button>
          <button
            type="button"
            class="toolbar-dialog__btn toolbar-dialog__btn--confirm"
            @click="insertLink"
          >
            {{ $t('Insert') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Embed Dialog -->
    <div v-if="showEmbedDialog" class="toolbar-dialog">
      <div class="toolbar-dialog__backdrop" @click="closeEmbedDialog"></div>
      <div class="toolbar-dialog__content">
        <label class="toolbar-dialog__label">{{ $t('Embed URL') }}</label>
        <input
          ref="embedInput"
          v-model="embedUrl"
          type="url"
          class="toolbar-dialog__input"
          :placeholder="$t('Paste YouTube, Vimeo, Loom, Figma, or other URL')"
          @keydown.enter.prevent="insertEmbed"
          @keydown.esc="closeEmbedDialog"
        />
        <p class="toolbar-dialog__hint">
          {{ $t('Supports YouTube, Vimeo, Loom, Figma, Miro, Twitter, GitHub') }}
        </p>
        <div class="toolbar-dialog__actions">
          <button
            type="button"
            class="toolbar-dialog__btn toolbar-dialog__btn--cancel"
            @click="closeEmbedDialog"
          >
            {{ $t('Cancel') }}
          </button>
          <button
            type="button"
            class="toolbar-dialog__btn toolbar-dialog__btn--confirm"
            @click="insertEmbed"
          >
            {{ $t('Embed') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { TOOLBAR_BUTTONS } from './editorConfig';

// Clean, production-ready icons using consistent 24px viewBox
// Based on Heroicons v2 with adjustments for clarity at small sizes

const BoldIcon = {
  template: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
    <path stroke-linejoin="round" d="M6.75 3.744h-.753v8.25h7.125a4.125 4.125 0 0 0 0-8.25H6.75Zm0 0v.38m0 16.122h6.747a4.5 4.5 0 0 0 0-9.001h-7.5v9h.753Zm0 0v-.37m0-15.751h6a3.75 3.75 0 1 1 0 7.5h-6m0-7.5v7.5m0 0v8.25m0-8.25h6.375a4.125 4.125 0 0 1 0 8.25H6.75m.747-15.38h4.875a3.375 3.375 0 0 1 0 6.75H7.497v-6.75Zm0 7.5h5.25a3.75 3.75 0 0 1 0 7.5h-5.25v-7.5Z" />
  </svg>`
};

const ItalicIcon = {
  template: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
    <path stroke-linecap="round" stroke-linejoin="round" d="M5.248 20.246H9.05m0 0h3.696m-3.696 0 5.893-16.502m0 0h-3.697m3.697 0h3.803" />
  </svg>`
};

const StrikethroughIcon = {
  template: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
    <path stroke-linecap="round" stroke-linejoin="round" d="M12 12a8.912 8.912 0 0 1-.318-.079c-1.585-.424-2.904-1.247-3.76-2.236-.873-1.009-1.265-2.19-.968-3.301.59-2.2 3.663-3.29 6.863-2.432A8.186 8.186 0 0 1 16.5 5.21M6.42 17.81c.857.99 2.176 1.812 3.761 2.237 3.2.858 6.274-.23 6.863-2.431.233-.868.044-1.779-.465-2.617M3.75 12h16.5" />
  </svg>`
};

const LinkIcon = {
  template: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
    <path stroke-linecap="round" stroke-linejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244" />
  </svg>`
};

const HeadingIcon = {
  template: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
    <path stroke-linecap="round" stroke-linejoin="round" d="M2.243 4.493v7.5m0 0v7.502m0-7.501h10.5m0-7.5v7.5m0 0v7.501m4.501-8.627 2.25-1.5v10.126m0 0h-2.25m2.25 0h2.25" />
  </svg>`
};

const QuoteIcon = {
  template: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M4.583 17.321C3.553 16.227 3 15 3 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311 1.804.167 3.226 1.648 3.226 3.489a3.5 3.5 0 0 1-3.5 3.5 3.871 3.871 0 0 1-2.748-1.179Zm10 0C13.553 16.227 13 15 13 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311 1.804.167 3.226 1.648 3.226 3.489a3.5 3.5 0 0 1-3.5 3.5 3.871 3.871 0 0 1-2.748-1.179Z"/>
  </svg>`
};

const CodeIcon = {
  template: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
    <path stroke-linecap="round" stroke-linejoin="round" d="M17.25 6.75 22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3-4.5 16.5" />
  </svg>`
};

const ListBulletIcon = {
  template: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
    <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0ZM3.75 12h.007v.008H3.75V12Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm-.375 5.25h.007v.008H3.75v-.008Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
  </svg>`
};

const ListNumberIcon = {
  template: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
    <path stroke-linecap="round" stroke-linejoin="round" d="M8.242 5.992h12m-12 6.003H20.24m-12 5.999h12M4.117 7.495v-3.75H2.99m1.125 3.75H2.99m1.125 0H5.24m-1.92 2.577a1.125 1.125 0 1 1 1.591 1.59l-1.83 1.83h2.16M2.99 15.745h1.125a1.125 1.125 0 0 1 0 2.25H3.74m0-.002h.375a1.125 1.125 0 0 1 0 2.25H2.99" />
  </svg>`
};

const VideoIcon = {
  template: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
    <path stroke-linecap="round" stroke-linejoin="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
  </svg>`
};

const PhotoIcon = {
  template: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
    <path stroke-linecap="round" stroke-linejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
  </svg>`
};

const ICON_MAP = {
  bold: BoldIcon,
  italic: ItalicIcon,
  strikethrough: StrikethroughIcon,
  link: LinkIcon,
  heading: HeadingIcon,
  quote: QuoteIcon,
  code: CodeIcon,
  'list-bullet': ListBulletIcon,
  'list-number': ListNumberIcon,
  video: VideoIcon,
  photo: PhotoIcon
};

export default {
  name: 'EditorToolbar',

  components: {
    BoldIcon,
    ItalicIcon,
    StrikethroughIcon,
    LinkIcon,
    HeadingIcon,
    QuoteIcon,
    CodeIcon,
    ListBulletIcon,
    ListNumberIcon,
    VideoIcon,
    PhotoIcon
  },

  props: {
    editor: {
      type: Object,
      default: null
    },
    buttonGroups: {
      type: Array,
      default: () => []
    },
    showToolbar: {
      type: Boolean,
      default: true
    },
    activeAttributes: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      hoveredButton: null,
      showLinkDialog: false,
      showEmbedDialog: false,
      linkUrl: '',
      embedUrl: ''
    };
  },

  methods: {
    getButtonConfig(buttonId) {
      return TOOLBAR_BUTTONS[buttonId] || {};
    },

    getIcon(buttonId) {
      const config = this.getButtonConfig(buttonId);
      return ICON_MAP[config.icon] || null;
    },

    getLabel(buttonId) {
      const config = this.getButtonConfig(buttonId);
      return this.$t(config.label || buttonId);
    },

    /**
     * Formatting buttons toggle a state and report it with aria-pressed.
     * Buttons that open a dialog (link, image, embed) are plain actions, so
     * marking them pressed would misdescribe them.
     */
    isToggle(buttonId) {
      const config = this.getButtonConfig(buttonId);
      return !!(config && config.trixAttribute);
    },

    getShortcut(buttonId) {
      const config = this.getButtonConfig(buttonId);
      return config.shortcut || null;
    },

    isActive(buttonId) {
      const config = this.getButtonConfig(buttonId);
      if (!config.trixAttribute) return false;
      return this.activeAttributes[config.trixAttribute] || false;
    },

    isDisabled(buttonId) {
      return !this.editor;
    },

    handleClick(buttonId) {
      if (!this.editor) return;

      const config = this.getButtonConfig(buttonId);

      if (config.isDialog) {
        if (buttonId === 'link') {
          this.openLinkDialog();
        }
        return;
      }

      if (config.isAction) {
        this.$emit('action', config.action);
        if (buttonId === 'embed') {
          this.openEmbedDialog();
        }
        return;
      }

      // Toggle Trix attribute
      if (config.trixAttribute) {
        if (this.isActive(buttonId)) {
          this.editor.deactivateAttribute(config.trixAttribute);
        } else {
          this.editor.activateAttribute(config.trixAttribute);
        }
        this.$emit('attribute-changed');
      }
    },

    openLinkDialog() {
      // Get current selection's link if any
      const currentLink = this.editor.getDocument().getCommonAttributesAtRange(
        this.editor.getSelectedRange()
      ).href;
      this.linkUrl = currentLink || '';
      this.showLinkDialog = true;
      this.$nextTick(() => {
        if (this.$refs.linkInput) {
          this.$refs.linkInput.focus();
          this.$refs.linkInput.select();
        }
      });
    },

    closeLinkDialog() {
      this.showLinkDialog = false;
      this.linkUrl = '';
    },

    insertLink() {
      if (!this.linkUrl) {
        this.closeLinkDialog();
        return;
      }

      // Ensure URL has protocol
      let url = this.linkUrl.trim();
      if (!/^https?:\/\//i.test(url)) {
        url = 'https://' + url;
      }

      // Check if there's a selection
      const range = this.editor.getSelectedRange();
      if (range[0] === range[1]) {
        // No selection, insert URL as link text
        this.editor.insertString(url);
        const newRange = this.editor.getSelectedRange();
        this.editor.setSelectedRange([newRange[0] - url.length, newRange[0]]);
      }

      // Apply link attribute
      this.editor.activateAttribute('href', url);

      this.closeLinkDialog();
      this.$emit('attribute-changed');
    },

    openEmbedDialog() {
      this.embedUrl = '';
      this.showEmbedDialog = true;
      this.$nextTick(() => {
        if (this.$refs.embedInput) {
          this.$refs.embedInput.focus();
        }
      });
    },

    closeEmbedDialog() {
      this.showEmbedDialog = false;
      this.embedUrl = '';
    },

    insertEmbed() {
      if (!this.embedUrl) {
        this.closeEmbedDialog();
        return;
      }

      this.$emit('insert-embed', this.embedUrl.trim());
      this.closeEmbedDialog();
    }
  }
};
</script>

<style scoped>
.tooltip-fade-enter-active,
.tooltip-fade-leave-active {
  transition: opacity 150ms ease-in-out;
}

.tooltip-fade-enter,
.tooltip-fade-leave-to {
  opacity: 0;
}

.toolbar-icon {
  width: 15px;
  height: 15px;
  flex-shrink: 0;
}
</style>
